import { defaultInputValues as allInputValueDefaults, defaultSimpleStructuralRiskBandsConfig } from "@/features/risk_bands";
import { TrackingConfig } from "@/features/tracking";
import { Feature, feature, Polygon } from "@turf/turf";
import Link from "next/link";
import { ArchetypeConfig } from "../risk_bands/schema";
import { allThemes } from "./themes";
import defaultTheme from "./themes/_default/index";
import { HubTheme } from "./types";

/**
 * Returns the current theme of this instance of Hub.
 * @returns Theme of this instance.
 */
export const getTheme = (): HubTheme => {
  return typeof process !== "undefined" && process.env.NEXT_PUBLIC_THEME_NAME ? allThemes[process.env.NEXT_PUBLIC_THEME_NAME] : defaultTheme;
};

/**
 * Returns the theme configuration of aesthetic choices.
 * @returns Aesthetics configuration data.
 */
export const getAestheticsConfig = () => {
  return getTheme().meta.aesthetics;
};

/**
 * Returns the name of the Hub which is contained within the theme.
 * @returns Custom name of Hub.
 */
export const getAppName = () => {
  return getTheme().meta.name;
};
export const getCommsConfig = () => {
  return getTheme().meta.config.featureSet.comms;
};
export const getDocsConfig = () => {
  return getTheme().meta.config.featureSet.docs;
};
export const getKbConfig = () => {
  return getTheme().meta.config.featureSet.docs.knowledgeBase;
};
export const getLocaleConfig = () => {
  return getTheme().meta.config.locale;
};

/**
 * Returns the specific configuration of the current instance of Hub.
 * @returns Configuration within theme.
 */
export const getConfig = () => {
  return getTheme().meta.config;
};

/**
 * Returns the risk bands mode of the current instance.
 * @returns Current risk bands mode ("abc", "halo", "simple").
 */
export const getRiskBandsMode = () => {
  return getConfig().riskBands.mode;
};

/**
 * Returns the maximum letter supported by the letter grading system.
 * @returns Expected to be "E" for "halo", "C" for everything else, but pulls
 * from branding..
 */
export const getRiskBandsMaxLetter = (regionType?: string) => {
  if (regionType === "global") return defaultSimpleStructuralRiskBandsConfig.maxLetter;
  return getConfig().riskBands.maxLetter;
};
export const getHazardList = () => {
  return getConfig().hazards;
};

/**
 * Determines if an archetype has an alias, and if so, returns alias instead of value.
 * @param value - string
 * @returns The real value for the archetype.
 */
export function dealiasArchetype<T>(value?: string) {
  return value ? (getArchetypeByValue(value)?.aliasOf || value) as T : undefined;
}
export const getArchetypes = (regionType?: string): ArchetypeConfig[] => {
  if (regionType === "global") return defaultSimpleStructuralRiskBandsConfig.archetypes;
  return getConfig().riskBands.archetypes;
};

/**
 * Gets the Archetype, by value.
 */
export const getArchetypeByValue = (value: string, regionType?: string) => {
  return getArchetypes(regionType).find(archetype => archetype.value === value);
};

/**
 * Builds a feature containing the coordinate bounds, if they exist.
 * @returns Coordinate bounds as a feature, or undefined.
 */
export const getCoordinateBounds = (): Feature<Polygon> | undefined => {
  const bounds = getConfig().geocoding.coordinateRestrictions.bounds;
  return bounds ? feature(bounds) : undefined;
};
export const getDefaultInputValues = (archetypeValue = "", regionType?: string) => {
  const localeConfig = getLocaleConfig();
  const archetype = getArchetypes(regionType).find(({
    value
  }) => value === archetypeValue);
  if (archetype) {
    const currencyKey = (localeConfig.defaultCurrency?.toLowerCase() ?? "aud") as keyof typeof allInputValueDefaults;
    const categoryKey = archetype.category === "industrial" ? "commercial" : archetype.category;
    const categoryDefaults = allInputValueDefaults[currencyKey]?.[categoryKey];
    if (categoryDefaults) return categoryDefaults;
  }
  return localeConfig.defaultInputValues;
};
export const getPages = () => {
  return getConfig().pages;
};

/**
 * Returns the current currency used by the system.
 */
export const getCurrencyCode = () => {
  // TODO: Load this in from some form of settings once able to.
  return getLocaleConfig().defaultCurrency;
};
export const getTrackingConfig = (): TrackingConfig => {
  return getConfig().featureSet.tracking;
};
export const getSettings = () => {
  return getTheme().meta.config.featureSet.settings;
};
export const getBaseThemeName = () => {
  if (process.env.NEXT_PUBLIC_THEME_NAME?.startsWith("hkma")) {
    return "hkma";
  }
  return "xdi";
};
export const isTpuLabellingEnabled = (regionType?: string) => {
  if (getBaseThemeName() !== "hkma") return false;
  if (regionType !== undefined && regionType !== "hk") return false;
  return getTheme().meta.config.tpuLabelling?.enabled || false;
};
export const getContactSupportLink = () => {
  const defaultContactSupportText = "contact support";

  // case xdi: has dedicated support url
  const {
    enabled: supportEnabled,
    url: supportUrl
  } = getTheme().meta.config.featureSet.comms.support;
  // hard coded disable for hkma
  if (supportEnabled && supportUrl && getBaseThemeName() !== "hkma") {
    return <Link href={supportUrl}>contact support</Link>;
  }

  // case hkma: has emailSupport, independent of support.enabled config
  const {
    enabled: hasEmailSupport,
    email: supportEmail
  } = getTheme().meta.config.featureSet.comms.emailSupport;
  if (hasEmailSupport && supportEmail) {
    return <Link href={`mailto:${supportEmail}`}>contact support</Link>;
  }

  // case: tcrg/default no support link, show text
  return defaultContactSupportText;
};

/**
 * The href behind getContactSupportLink, for callers which need to supply their own link element.
 * @returns A support URL, or undefined where this theme has no support destination.
 */
export const getContactSupportLinkUrl = (): string | undefined => {
  // case xdi: has dedicated support url
  const {
    enabled: supportEnabled,
    url: supportUrl
  } = getTheme().meta.config.featureSet.comms.support;
  // hard coded disable for hkma
  if (supportEnabled && supportUrl && getBaseThemeName() !== "hkma") {
    return supportUrl;
  }

  // case hkma: has emailSupport, independent of support.enabled config
  const {
    enabled: hasEmailSupport,
    email: supportEmail
  } = getTheme().meta.config.featureSet.comms.emailSupport;
  if (hasEmailSupport && supportEmail) {
    return `mailto:${supportEmail}`;
  }

  // case: tcrg/default no support link
  return undefined;
};